import React from 'react';
import { GraduationCap, Sparkles, MessageCircle, FileText, Mail, Phone, HelpCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { preloadRoute } from '@/hooks/useRoutePreload';

const ResourcesMegaMenu: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const bgColor = isDark ? '#1a1a1a' : '#FCFCF9';
  const hoverBg = isDark ? '#2a2a2a' : '#F5F5F2';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const labelColor = isDark ? '#888888' : '#888888';
  const titleColor = isDark ? '#FCFCF9' : '#111111';
  const descColor = isDark ? '#999999' : '#555555';
  const iconColor = isDark ? '#888888' : '#888888';
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  return (
    <div className="w-[650px] flex" style={{ backgroundColor: bgColor }}>
      {/* Main Content Area */}
      <div className="flex-1 p-6">
        {/* LEARN Category */}
        <div className="mb-6">
          <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
            Learn
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <a href="/academy" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/academy'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <GraduationCap className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium" style={{ color: titleColor }}>Academy</h4>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#990f3d', color: 'white' }}>
                    Popular
                  </span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: descColor }}>Learn from expert courses</p>
              </div>
            </a>
            <a href="/free-marketing" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/free-marketing'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <Sparkles className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-sm font-medium" style={{ color: titleColor }}>Free Marketing</h4>
                <p className="text-xs mt-0.5" style={{ color: descColor }}>Expert tips & proven tactics</p>
              </div>
            </a>
          </div>
        </div>

        {/* SUPPORT Category */}
        <div>
          <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
            Support
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <a href="/help" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/help'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <MessageCircle className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-sm font-medium" style={{ color: titleColor }}>Help Center</h4>
                <p className="text-xs mt-0.5" style={{ color: descColor }}>Find instant answers</p>
              </div>
            </a>
            <a href="/blog" className="group flex items-start gap-3 p-2 rounded-md transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; preloadRoute('/blog'); }} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <FileText className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-sm font-medium" style={{ color: titleColor }}>Articles</h4>
                <p className="text-xs mt-0.5" style={{ color: descColor }}>Latest insights & updates</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="w-[235px] py-6 pl-6 pr-10 border-l" style={{ borderColor }}>
        <h3 className="text-[11px] font-medium uppercase tracking-wider mb-4" style={{ color: labelColor }}>
          Get in Touch
        </h3>
        <div className="space-y-3">
          <a href="mailto:hello@storymarketing.group" className="group flex items-center gap-2 text-sm transition-colors hover:underline" style={{ color: descColor }}>
            <Mail className="w-4 h-4 flex-shrink-0" style={{ color: iconColor }} strokeWidth={1.5} />
            <span className="text-xs">hello@storymarketing.group</span>
          </a>
          <a href="tel:+18885056258" className="group flex items-center gap-2 text-sm transition-colors hover:underline" style={{ color: descColor }}>
            <Phone className="w-4 h-4 flex-shrink-0" style={{ color: iconColor }} strokeWidth={1.5} />
            <span className="text-xs">(888) 505-6258</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResourcesMegaMenu;
